/**
 * The hero's rocket layer: the gate in front of the scene.
 *
 * The scene itself is three.js and weighs what three.js weighs, so nothing here
 * imports it. This component decides whether the machine in front of it should
 * be running a WebGL background at all, and only then fetches the chunk. A
 * phone, a visitor who has asked for less motion, and a board with nothing on
 * it all cost exactly one module — this one — and no bytes of three.
 *
 * The gates, in the order they are checked:
 *
 *   - `prefers-reduced-motion` — no scene. The hero keeps its painted gradient,
 *     which is what it looked like before this layer existed.
 *   - narrower than 768px — no scene. The mobile hero is a single column of
 *     type over a small board; it has no spare depth to put anything behind,
 *     and the machines that render it are the ones least able to spare a GPU.
 *   - an empty board — no scene, because there is nothing true to fly.
 *
 * Once it is running it stops running the moment it is not being looked at: a
 * hidden tab and a hero scrolled off the top both park the render loop, so the
 * cost of this layer for a visitor reading the leaderboard below is zero.
 */

import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import type { RankedEntry } from '@bidtohire/shared';

import { useLiveBids } from '../lib/live';
import type { RocketScene } from '../lib/rocket-scene';
import { rocketRowFromBid, rocketRowsFromEntries } from '../lib/rocket-rows';

/** Below this the hero is a single column, and the scene is skipped whole. */
export const ROCKET_MIN_WIDTH = 768;

/** Clearance kept between the rightmost card and the Live Board, in pixels. */
const BOARD_CLEARANCE = 28;

function matches(query: string): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia(query).matches;
}

/** Watch a media query, re-rendering when it flips. */
function useMediaQuery(query: string): boolean {
  const [state, setState] = useState(() => matches(query));
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const list = window.matchMedia(query);
    const onChange = (): void => {
      setState(list.matches);
    };
    onChange();
    list.addEventListener('change', onChange);
    return () => {
      list.removeEventListener('change', onChange);
    };
  }, [query]);
  return state;
}

export function RocketField({ entries }: { readonly entries: readonly RankedEntry[] }): ReactNode {
  const reduced = useMediaQuery('(prefers-reduced-motion: reduce)');
  const wide = useMediaQuery(`(min-width: ${ROCKET_MIN_WIDTH}px)`);

  const rows = useMemo(() => rocketRowsFromEntries(entries), [entries]);
  const allowed = !reduced && wide && rows.length > 0;

  const host = useRef<HTMLDivElement | null>(null);
  const scene = useRef<RocketScene | undefined>(undefined);
  const rowsRef = useRef(rows);
  rowsRef.current = rows;
  /** Whether the layer is both on screen and in a tab someone is looking at. */
  const watched = useRef(false);

  // A cleared bid flies its own row, right now, with a heavier burst. This is
  // the only thing on the page that reacts to a payment other than the board
  // itself, and it carries the same listing the board is about to reorder.
  useLiveBids({
    onBid: (event) => {
      scene.current?.launch(rocketRowFromBid(event));
    },
  });

  useEffect(() => {
    const element = host.current;
    if (!allowed || element === null) return;

    let cancelled = false;

    void (async () => {
      const module = await import('../lib/rocket-scene');
      if (cancelled) return;
      const built = await module.createRocketScene({
        host: element,
        // The Live Board's column is airspace. It is measured from the board's
        // own box rather than assumed from the breakpoint, so a layout change
        // moves the keep-out with it. When the board is stacked under the copy
        // instead of beside it there is no column to avoid, and the whole frame
        // opens back up.
        measureKeepOut: () => {
          const board = element.parentElement?.querySelector('.liveboard');
          if (!(board instanceof HTMLElement)) return undefined;
          const frame = element.getBoundingClientRect();
          const box = board.getBoundingClientRect();
          if (frame.width <= 0) return undefined;
          if (box.left < frame.left + frame.width * 0.45) return undefined;
          const edge = box.left - BOARD_CLEARANCE - frame.left;
          return (edge / frame.width) * 2 - 1;
        },
      });
      if (cancelled) {
        built.dispose();
        return;
      }
      scene.current = built;
      built.setRows(rowsRef.current);
      built.setRunning(watched.current);
    })();

    return () => {
      cancelled = true;
      scene.current?.dispose();
      scene.current = undefined;
    };
  }, [allowed]);

  // Rows in, whenever the board read changes underneath us.
  useEffect(() => {
    scene.current?.setRows(rows);
  }, [rows]);

  // Run only while the hero is on screen and the tab is in front.
  useEffect(() => {
    const element = host.current;
    if (!allowed || element === null) return;

    let onScreen = true;
    const settle = (): void => {
      const next = onScreen && !document.hidden;
      watched.current = next;
      scene.current?.setRunning(next);
    };

    const onVisibility = (): void => {
      settle();
    };
    document.addEventListener('visibilitychange', onVisibility);

    let observer: IntersectionObserver | undefined;
    if (typeof IntersectionObserver === 'function') {
      onScreen = false;
      observer = new IntersectionObserver(
        (records) => {
          const last = records[records.length - 1];
          if (last === undefined) return;
          onScreen = last.isIntersecting;
          settle();
        },
        { threshold: 0 },
      );
      observer.observe(element);
    }
    settle();

    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      observer?.disconnect();
      watched.current = false;
    };
  }, [allowed]);

  if (!allowed) return null;
  return <div className="hero-rockets" ref={host} aria-hidden="true" />;
}
